// WHAT: Settings view component for MiracleFlow productivity integration configuration
// WHY:  Provides interface for viewing and managing MiracleFlow settings stored in Supabase database
// DOMAIN: Settings → MiracleFlow
// DEPENDENCIES: Tauri core API, Supabase database configuration
// RISKS:
//   - NOTE: Configuration is read-only in this view - changes must be made in MiracleFlow app
//   - NOTE: Relies on Supabase database connection for configuration persistence
//   - WARN: Error handling may not provide detailed feedback for connection issues

import { core } from "@tauri-apps/api";
import { Zap, Database, Settings, CheckCircle, Info, Shield, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";

import { Card, Stack, Heading, Badge, StatusIndicator } from "@/components/ui";

/**
 * WHAT:
 *   React component that displays MiracleFlow productivity integration settings and status.
 *
 * WHY:
 *   Provides users with visibility into their MiracleFlow configuration status and
 *   information about how settings are managed through the Supabase database.
 *
 * BEHAVIOR:
 *   - Loads configuration status on mount via Tauri command
 *   - Displays configuration status (configured/not configured)
 *   - Shows information about database-based configuration management
 *   - Displays security and data management information
 *   - Handles loading and error states
 *
 * RISKS:
 *   - NOTE: Configuration is read-only - users must use MiracleFlow app to modify settings
 *   - WARN: Depends on Supabase database connection - may fail if database unavailable
 *   - NOTE: Error messages may not provide detailed diagnostic information
 *
 * @returns {JSX.Element} MiracleFlow settings view component
 *
 */
export function MiracleFlowSettingsView() {
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  /**
   * WHAT:
   *   Loads MiracleFlow configuration from the backend and updates component state.
   *
   * WHY:
   *   Fetches current configuration status to determine if MiracleFlow is properly configured.
   *
   * SIDE EFFECTS:
   *   - Updates loading state during async operation
   *   - Updates error state on failure
   *   - Updates configuration state and isConfigured flag
   *
   * RISKS:
   *   - WARN: May fail silently if backend command is unavailable
   *   - NOTE: Configuration check is based on object key existence, not validation
   */
  /**
   * WHAT:
   *   Loads the MiracleFlow configuration from the Tauri backend.
   *
   * WHY:
   *   To determine if MiracleFlow is configured and display the current status.
   *
   * BEHAVIOR:
   *   - Invokes `get_json_config_cmd` to fetch the full configuration.
   *   - Checks if `config.MiracleFlow` exists and has properties.
   *   - Sets `isConfigured` based on the presence of configuration data.
   *   - Handles errors by setting error state and marking as not configured.
   *
   * RISKS:
   *   - WARN: If the Tauri command fails, the component will show an error state.
   */
  interface MiracleFlowConfig {
    [key: string]: unknown;
    MiracleFlow?: Record<string, unknown>;
  }

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const configData = await core.invoke<MiracleFlowConfig>("get_json_config_cmd");
      // NOTE: Check if MiracleFlow configuration exists by verifying object structure
      // WARN: This validation only checks for key existence, not configuration validity
      setIsConfigured(
        Boolean(configData.MiracleFlow && Object.keys(configData.MiracleFlow ?? {}).length > 0),
      );
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Failed to load MiracleFlow configuration";
      setError(`Failed to load MiracleFlow configuration: ${message}`);
      setIsConfigured(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <Zap className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              MiracleFlow Integration
            </Heading>
            <p className="text-slate-300">
              Manage your MiracleFlow configuration and productivity settings for enhanced workflow
              optimization.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Status Indicators */}
      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <Settings className="w-5 h-5 text-slate-400" />
          Configuration Status
        </Heading>

        <StatusIndicator
          description="Your MiracleFlow productivity settings and workflow configurations"
          isConfigured={isConfigured}
          label="MiracleFlow Configuration"
          showLock
          size="md"
        />
      </div>

      {/* Information Card */}
      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-400 mb-1">
              MiracleFlow Productivity Integration
            </h3>
            <p className="text-sm text-blue-300">
              MiracleFlow settings are managed through the Supabase database for better persistence,
              synchronization, and cross-device accessibility. Your productivity configurations are
              automatically saved and synchronized.
            </p>
          </div>
        </Stack>
      </Card>

      {/* Configuration Status */}
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          {/* Header */}
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
              <Database className="w-5 h-5 text-tracq-bright-blue" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              Database Configuration
            </Heading>
          </Stack>

          {/* Status Display */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-slate-300">Configuration Source:</span>
              <Badge size="sm" style="subtle" variant="default">
                Supabase Database
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-300">Synchronization:</span>
              <Badge size="sm" style="subtle" variant="success">
                ✓ Active
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-300">Persistence:</span>
              <Badge size="sm" style="subtle" variant="success">
                ✓ Enabled
              </Badge>
            </div>
          </div>

          {/* Current Setup Information */}
          <Card className="bg-slate-700/50" padding="md" variant="default">
            <Stack direction="col" gap={3}>
              <Heading className="text-md font-medium text-white" level={4}>
                Current Setup
              </Heading>
              <div className="text-sm text-slate-300 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Configuration loaded from Supabase database</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Settings are automatically synchronized</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Changes are persisted immediately</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Cross-device accessibility enabled</span>
                </div>
              </div>
            </Stack>
          </Card>

          {/* Management Content */}
          <Stack direction="col" gap={2}>
            <p className="text-slate-300 text-sm">
              Your MiracleFlow configuration is managed through the Supabase database integration.
              This ensures that your productivity settings are automatically synchronized across all
              your devices and persisted securely.
            </p>
            <p className="text-slate-500 text-xs">
              To modify MiracleFlow settings, use the MiracleFlow application interface where
              changes will be automatically saved to the database.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Error State */}
      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <CheckCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">{error}</span>
          </Stack>
        </Card>
      ) : null}

      {/* Loading State */}
      {loading ? (
        <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            <span className="text-slate-400 text-sm">Loading MiracleFlow configuration...</span>
          </Stack>
        </Card>
      ) : null}

      {/* Security Notice */}
      <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-400 mb-1">Security & Data Management</h3>
            <p className="text-sm text-green-300">
              Your MiracleFlow configuration is stored securely in the Supabase database with
              encryption at rest and in transit. All settings are automatically synchronized and
              backed up, ensuring data integrity and availability.
            </p>
          </div>
        </Stack>
      </Card>

      {/* Help Section */}
      <Card className="bg-slate-800/20 border-slate-700/30" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-1">
              How to configure MiracleFlow
            </h3>
            <div className="text-sm text-slate-400 space-y-1">
              <p>• Open the MiracleFlow application from the main navigation</p>
              <p>• Configure your productivity settings and workflows</p>
              <p>• Settings are automatically saved to the database</p>
              <p>• Changes are synchronized across all devices</p>
              <p>• No manual configuration required in this settings panel</p>
            </div>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
